/**
 * Compatibility hook that provides a history-like API using React Router v7's useNavigate
 */
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import React from 'react';

// This hook provides an API that resembles the old useHistory hook from React Router v5
// It wraps useNavigate from React Router v7 to maintain backwards compatibility
const useHistory = () => {
  const navigate = useNavigate();

  return {
    // The main method from useHistory that we need to support
    push: (path, state) => navigate(path, { state }),
    replace: (path, state) => navigate(path, { replace: true, state }),
    go: (delta) => navigate(delta),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    // Any other methods needed can be added here...
  };
};

/**
 * withRouter HOC replacement for React Router v7 compatibility
 * Provides location, navigate, and params as props to the wrapped component
 */
export const withRouter = (Component) => {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    
    return (
      <Component
        {...props}
        location={location}
        navigate={navigate}
        params={params}
        // For backwards compatibility with withRouter from v5
        history={{ 
          push: (path, state) => navigate(path, { state }),
          replace: (path, state) => navigate(path, { replace: true, state }),
          goBack: () => navigate(-1),
          goForward: () => navigate(1),
          location
        }}
        match={{ params }}
      />
    );
  }
  
  // Display name for better debugging
  const displayName = Component.displayName || Component.name || 'Component';
  ComponentWithRouterProp.displayName = `withRouter(${displayName})`;
  
  return ComponentWithRouterProp;
};

export default useHistory;